import { z } from 'zod';

const positive = z.number().positive();
const nonNegative = z.number().nonnegative();
const size = z.object({ width: positive, height: positive });
const insets = z.object({
  top: nonNegative,
  right: nonNegative,
  bottom: nonNegative,
  left: nonNegative,
});
const viewId = z.enum(['front', 'back', 'left-sleeve', 'right-sleeve']);
const sizeSurfaceGeometry = z.object({
  placementAreaMm: size,
  maxTransferSizeMm: size.optional(),
});
const sizeProfile = z.object({
  id: z.string().min(1),
  size: z.string().min(1),
  garmentMm: z.object({
    flatWidth: positive,
    bodyLength: positive,
    sleeveLength: positive,
  }),
  source: z.object({
    brand: z.string().min(1),
    model: z.string().min(1),
    productCode: z.string().min(1),
    url: z.string().url(),
    method: z.literal('lay-flat'),
  }),
  surfaces: z.object({
    front: sizeSurfaceGeometry,
    back: sizeSurfaceGeometry,
    'left-sleeve': sizeSurfaceGeometry.optional(),
    'right-sleeve': sizeSurfaceGeometry.optional(),
  }),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Convert legacy v1 catalogue payloads to the one canonical v2 in-memory shape. */
function normalizeProductConfigInput(input: unknown): unknown {
  if (!isRecord(input) || input.schemaVersion !== 1) return input;

  const legacyColours = Array.isArray(input.colours) ? input.colours : [];
  const legacyProfiles = Array.isArray(input.sizeProfiles) ? input.sizeProfiles : [];
  const hasVariantColourRules = legacyColours.some(
    colour => isRecord(colour) && Array.isArray(colour.availableVariantIds),
  );
  const colours = legacyColours.map(colour => {
    if (!isRecord(colour)) return colour;
    const canonicalColour = { ...colour };
    delete canonicalColour.availableVariantIds;
    return canonicalColour;
  });
  const variants = (Array.isArray(input.variants) ? input.variants : []).map(variant => {
    if (!isRecord(variant)) return variant;
    const legacyVariantId = typeof variant.id === 'string' ? variant.id : '';
    const migrateClassicTeeToMen =
      input.id === 'malfini-classic-new-132' && legacyVariantId === 'men';
    const variantId = legacyVariantId;
    const nestedProfiles = legacyProfiles.flatMap(profile => {
      if (!isRecord(profile) || profile.variantId !== legacyVariantId) return [];
      const nestedProfile = { ...profile };
      delete nestedProfile.variantId;
      return [nestedProfile];
    });
    const colourIds = hasVariantColourRules
      ? legacyColours.flatMap(colour => {
          if (!isRecord(colour) || typeof colour.id !== 'string') return [];
          const availableVariantIds = colour.availableVariantIds;
          return !Array.isArray(availableVariantIds) ||
            availableVariantIds.includes(legacyVariantId)
            ? [colour.id]
            : [];
        })
      : undefined;
    return {
      ...variant,
      id: variantId,
      ...(migrateClassicTeeToMen
        ? { legacyIds: ['unisex'], label: 'Pánske', audience: 'men', defaultSize: 'M' }
        : {}),
      ...(colourIds ? { colourIds } : {}),
      ...(nestedProfiles.length ? { sizeProfiles: nestedProfiles } : {}),
    };
  });
  const canonicalProduct = { ...input };
  delete canonicalProduct.sizeProfiles;
  return { ...canonicalProduct, schemaVersion: 2, colours, variants };
}

export const productConfigSchema = z.preprocess(
  normalizeProductConfigInput,
  z
    .object({
      schemaVersion: z.literal(2),
      id: z.string().min(1),
      name: z.string().min(1),
      slug: z.string().regex(/^[a-z0-9-]+$/),
      configuratorSlug: z
        .string()
        .regex(/^[a-z0-9-]+$/)
        .optional(),
      garmentType: z.enum(['t-shirt', 'hoodie', 'long-sleeve']),
      description: z.string(),
      basePriceCents: z.number().int().nonnegative(),
      currency: z.literal('EUR'),
      defaultColourId: z.string().min(1),
      colours: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            hex: z.string().regex(/^#[0-9a-f]{6}$/i),
          }),
        )
        .min(1),
      variants: z
        .array(
          z.object({
            id: z.string(),
            legacyIds: z.array(z.string().min(1)).min(1).optional(),
            label: z.string(),
            audience: z.enum(['men', 'women', 'kids', 'unisex']),
            mockupImages: z.partialRecord(viewId, z.string().min(1)).optional(),
            previewAreaScale: z.object({ x: positive.max(2), y: positive.max(2) }).optional(),
            colourIds: z.array(z.string().min(1)).min(1).optional(),
            defaultColourId: z.string().min(1).optional(),
            sizes: z.array(z.string()).min(1),
            selectableSizes: z.array(z.string()).min(1).optional(),
            defaultSize: z.string().min(1).optional(),
            sizeProfiles: z.array(sizeProfile).min(1).optional(),
            priceDeltaCents: z.number().int().optional(),
          }),
        )
        .min(1),
      printableAreas: z
        .array(
          z.object({
            viewId,
            label: z.string(),
            sizeMm: size,
            safeZoneMm: insets,
            bleedMm: insets,
            maxPrintSizeMm: size,
            mockupImage: z.string(),
            garmentMask: z.string().optional(),
            previewArea: z.object({
              xPct: z.number(),
              yPct: z.number(),
              widthPct: positive,
              heightPct: positive,
            }),
            previewTransform: z
              .object({
                x: z.number(),
                y: z.number(),
                scaleX: z.number(),
                scaleY: z.number(),
                rotationDeg: z.number(),
              })
              .optional(),
            supportedTechnologies: z
              .array(z.enum(['dtf', 'transfer', 'dtg', 'screen-print']))
              .min(1),
          }),
        )
        .min(1),
      productionRules: z.object({
        minimumDpi: positive,
        recommendedDpi: positive,
        minimumTextHeightMm: positive,
        minimumDetailMm: positive,
        allowTransparency: z.boolean(),
        acceptedColourModes: z.array(z.enum(['srgb', 'rgb', 'cmyk', 'unknown'])),
        workflow: z
          .object({
            kind: z.literal('print-cut-transfer'),
            machine: z.string().min(1),
            rip: z.string().min(1),
            maximumMediaWidthMm: positive,
            maximumPrintCutWidthMm: positive,
            cutContourSpotName: z.string().regex(/^CutContour[A-Za-z0-9]*$/),
            pagePaddingMm: nonNegative,
            contourOffsetMm: nonNegative,
            contourResolutionPxPerMm: positive,
            minimumContourAreaMm2: nonNegative,
            mirror: z.boolean(),
          })
          .optional(),
        pressPlatenMm: size.optional(),
        requiresProductionCalibration: z.boolean().optional(),
      }),
      supportedTechnologies: z.array(z.enum(['dtf', 'transfer', 'dtg', 'screen-print'])).min(1),
    })
    .superRefine((product, context) => {
      const colourIds = new Set<string>();
      product.colours.forEach((colour, colourIndex) => {
        if (colourIds.has(colour.id)) {
          context.addIssue({
            code: 'custom',
            path: ['colours', colourIndex, 'id'],
            message: `Duplicate product colour ${colour.id}`,
          });
        }
        colourIds.add(colour.id);
      });

      const canonicalVariantIds = new Set<string>();
      product.variants.forEach((variant, variantIndex) => {
        if (canonicalVariantIds.has(variant.id)) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'id'],
            message: `Duplicate product variant ${variant.id}`,
          });
        }
        canonicalVariantIds.add(variant.id);
      });
      const acceptedVariantIds = new Set(canonicalVariantIds);
      const profileIds = new Set<string>();

      product.variants.forEach((variant, variantIndex) => {
        variant.legacyIds?.forEach((legacyId, legacyIndex) => {
          if (acceptedVariantIds.has(legacyId)) {
            context.addIssue({
              code: 'custom',
              path: ['variants', variantIndex, 'legacyIds', legacyIndex],
              message: `Duplicate or conflicting product variant alias ${legacyId}`,
            });
          }
          acceptedVariantIds.add(legacyId);
        });

        const configuredSizes = new Set(variant.sizes);
        if (configuredSizes.size !== variant.sizes.length) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'sizes'],
            message: `Duplicate configured size in variant ${variant.id}`,
          });
        }
        const selectableSizeList = variant.selectableSizes ?? variant.sizes;
        const selectableSizes = new Set(selectableSizeList);
        if (selectableSizes.size !== selectableSizeList.length) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'selectableSizes'],
            message: `Duplicate selectable size in variant ${variant.id}`,
          });
        }
        selectableSizeList.forEach(sizeLabel => {
          if (configuredSizes.has(sizeLabel)) return;
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'selectableSizes'],
            message: `Selectable size ${variant.id}/${sizeLabel} is not configured`,
          });
        });
        if (variant.defaultSize && !selectableSizes.has(variant.defaultSize)) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'defaultSize'],
            message: `Default size ${variant.id}/${variant.defaultSize} is not selectable`,
          });
        }

        const variantColourIds = variant.colourIds ?? product.colours.map(colour => colour.id);
        if (new Set(variantColourIds).size !== variantColourIds.length) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'colourIds'],
            message: `Duplicate colour in variant ${variant.id}`,
          });
        }
        variantColourIds.forEach(colourId => {
          if (colourIds.has(colourId)) return;
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'colourIds'],
            message: `Unknown colour ${variant.id}/${colourId}`,
          });
        });
        if (variant.defaultColourId && !variantColourIds.includes(variant.defaultColourId)) {
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'defaultColourId'],
            message: `Default colour ${variant.id}/${variant.defaultColourId} is not available`,
          });
        }

        if (!variant.sizeProfiles) return;
        const seenProfileSizes = new Set<string>();
        variant.sizeProfiles.forEach((profile, profileIndex) => {
          const profilePath: Array<string | number> = [
            'variants',
            variantIndex,
            'sizeProfiles',
            profileIndex,
          ];
          if (profileIds.has(profile.id)) {
            context.addIssue({
              code: 'custom',
              path: [...profilePath, 'id'],
              message: `Duplicate size profile id ${profile.id}`,
            });
          }
          profileIds.add(profile.id);
          if (seenProfileSizes.has(profile.size)) {
            context.addIssue({
              code: 'custom',
              path: [...profilePath, 'size'],
              message: `Duplicate size profile ${variant.id}/${profile.size}`,
            });
          }
          seenProfileSizes.add(profile.size);
          if (!configuredSizes.has(profile.size)) {
            context.addIssue({
              code: 'custom',
              path: profilePath,
              message: `Orphan size profile ${variant.id}/${profile.size}`,
            });
          }
          Object.entries(profile.surfaces).forEach(([surfaceId, geometry]) => {
            if (
              geometry?.maxTransferSizeMm &&
              (geometry.maxTransferSizeMm.width > geometry.placementAreaMm.width ||
                geometry.maxTransferSizeMm.height > geometry.placementAreaMm.height)
            ) {
              context.addIssue({
                code: 'custom',
                path: [...profilePath, 'surfaces', surfaceId, 'maxTransferSizeMm'],
                message: `${variant.id}/${profile.size}/${surfaceId}: transfer limit cannot exceed placement area`,
              });
            }
          });
        });
        variant.sizes.forEach(sizeLabel => {
          if (seenProfileSizes.has(sizeLabel)) return;
          context.addIssue({
            code: 'custom',
            path: ['variants', variantIndex, 'sizeProfiles'],
            message: `Missing size profile ${variant.id}/${sizeLabel}`,
          });
        });
      });

      if (!product.colours.some(colour => colour.id === product.defaultColourId)) {
        context.addIssue({
          code: 'custom',
          path: ['defaultColourId'],
          message: 'Default colour must exist in colours',
        });
      }
      const views = new Set<string>();
      for (const area of product.printableAreas) {
        if (views.has(area.viewId))
          context.addIssue({ code: 'custom', message: `Duplicate view ${area.viewId}` });
        views.add(area.viewId);
        if (
          area.maxPrintSizeMm.width > area.sizeMm.width ||
          area.maxPrintSizeMm.height > area.sizeMm.height
        ) {
          context.addIssue({
            code: 'custom',
            message: `${area.viewId}: max print cannot exceed printable area`,
          });
        }
      }

      const workflow = product.productionRules.workflow;
      if (workflow && workflow.maximumPrintCutWidthMm > workflow.maximumMediaWidthMm) {
        context.addIssue({
          code: 'custom',
          path: ['productionRules', 'workflow', 'maximumPrintCutWidthMm'],
          message: 'Printable/cuttable width cannot exceed the loaded media width',
        });
      }
      if (workflow && workflow.contourOffsetMm > workflow.pagePaddingMm) {
        context.addIssue({
          code: 'custom',
          path: ['productionRules', 'workflow', 'pagePaddingMm'],
          message: 'PDF page padding must contain the configured contour offset',
        });
      }
    }),
);

const transform = z.object({
  positionMm: z.object({ x: z.number(), y: z.number() }),
  sizeMm: size,
  rotationDeg: z.number(),
  flipX: z.boolean(),
  flipY: z.boolean(),
});

const baseObject = z.object({
  id: z.string(),
  name: z.string(),
  transform,
  opacity: z.number().min(0).max(1),
  visible: z.boolean(),
  locked: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const imageObject = baseObject.extend({
  type: z.literal('image'),
  assetId: z.string(),
  crop: z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: positive.max(1),
    height: positive.max(1),
  }),
  mask: z
    .object({ kind: z.enum(['circle', 'rounded-rect']), radiusMm: nonNegative.optional() })
    .optional(),
  transparentColour: z
    .object({
      colour: z.string().regex(/^#[\da-f]{6}$/i),
      tolerance: z.number().min(0).max(1),
    })
    .optional(),
  borders: z
    .array(
      z.object({
        colour: z.string().min(1),
        widthMm: nonNegative,
      }),
    )
    .max(3)
    .optional(),
  adjustments: z.object({
    brightness: z.number(),
    contrast: z.number(),
    saturation: z.number(),
    sharpness: z.number(),
    grayscale: z.boolean(),
  }),
  versionOf: z.string().optional(),
  aiOperationId: z.string().optional(),
});

const textObject = baseObject.extend({
  type: z.literal('text'),
  text: z.string(),
  fontFamily: z.string(),
  fontAssetId: z.string(),
  fontSizeMm: positive,
  fontWeight: z.number(),
  fontStyle: z.enum(['normal', 'italic']),
  colour: z.string(),
  align: z.enum(['left', 'center', 'right']),
  lineHeight: positive,
  letterSpacingMm: z.number(),
  stroke: z.object({ colour: z.string(), widthMm: nonNegative }).optional(),
  shadow: z
    .object({
      colour: z.string(),
      blurMm: nonNegative,
      offsetMm: z.object({ x: z.number(), y: z.number() }),
    })
    .optional(),
  curve: z.object({ radiusMm: positive, direction: z.enum(['up', 'down']) }).optional(),
});

const shapeObject = baseObject.extend({
  type: z.literal('shape'),
  shape: z.enum([
    'rectangle',
    'ellipse',
    'triangle',
    'diamond',
    'pentagon',
    'hexagon',
    'star',
    'heart',
    'arrow',
  ]),
  fill: z.string(),
  stroke: z.object({ colour: z.string(), widthMm: nonNegative }).optional(),
  cornerRadiusMm: nonNegative.optional(),
});

const drawingObject = baseObject.extend({
  type: z.literal('drawing'),
  points: z
    .array(
      z.object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
      }),
    )
    .min(2)
    .max(20_000),
  stroke: z.object({ colour: z.string().min(1), widthMm: positive }),
  smoothing: z.number().min(0).max(1),
});

/** Shared by document import and structured design-assistant operation results. */
export const designObjectSchema = z.discriminatedUnion('type', [
  imageObject,
  textObject,
  shapeObject,
  drawingObject,
]);

export const surfacePrintEffectSettingSchema = z.object({
  intensity: z.number().min(0).max(1),
  seed: z.number().int().min(0).max(0xffff_ffff),
});

export const surfacePrintEffectsSchema = z.object({
  algorithmVersion: z.literal(1),
  distress: surfacePrintEffectSettingSchema,
  washed: surfacePrintEffectSettingSchema,
  grain: surfacePrintEffectSettingSchema,
  cracks: surfacePrintEffectSettingSchema,
});

export const designDocumentSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  productId: z.string(),
  variantId: z.string(),
  size: z.string(),
  quantity: z.number().int().positive().default(1),
  colourId: z.string(),
  technology: z.enum(['dtf', 'transfer', 'dtg', 'screen-print']),
  surfaces: z.partialRecord(
    viewId,
    z.object({
      viewId,
      printAreaMm: size,
      objects: z.array(designObjectSchema),
      printEffects: surfacePrintEffectsSchema.optional(),
    }),
  ),
  assets: z.record(
    z.string(),
    z.object({
      id: z.string(),
      kind: z.enum(['image', 'font']),
      name: z.string(),
      mimeType: z.string(),
      sourceUrl: z.string(),
      checksum: z.string().optional(),
      pixelSize: z.object({ width: positive, height: positive }).optional(),
      colourMode: z.enum(['srgb', 'rgb', 'cmyk', 'unknown']).optional(),
      transparency: z.enum(['present', 'absent', 'unknown']).optional(),
      embedded: z.boolean(),
      license: z.string().optional(),
    }),
  ),
  acknowledgedWarnings: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
